import type { Currency } from '@ps/web-core-enums';
import React from 'react';
import { useField } from 'react-final-form';
import { useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import Translation from '@swf/components/Translation/Translation';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import { select } from '@swf/core/store';

import {
  convertAmountByRate,
  getCurrencyRate,
  isZeroExponentCurrency
} from '../../../../../../utils/cashier';
import Prompt from '../../../../../Prompt/Prompt';

import {
  TXTCLI_Deposit_in_N0,
  TXTWCL_Our_exchange_rate_is_N0_to_N1X,
  TXTWCL_Would_you_like_to_change_your__ELL,
  TXTWCL_Your_bank_may_charge_you_a_for_ELL_1,
  TXTWCL_Your_deposit_will_be_converted
} from './LocalCurrency.messages.json';
import { multiContent } from './LocalCurrency.scss';

interface Props {
  onClose(): void;
  onCloseCallback(): void;
}

const LocalCurrency: React.FC<Props> = ({ onClose, onCloseCallback }) => {
  const defaultCountryCurrency = useSelector(
    select.account.getDefaultCurrencyForCountry
  ) as Currency;
  const isDefaultCurrencyZeroExponent = isZeroExponentCurrency(defaultCountryCurrency);

  const {
    data: {
      paySystemDetails: {
        rates: { curr: balanceCurrency, rates }
      }
    }
  } = useWizardContext();

  const {
    input: {
      value: { amount, currency, ...rest },
      onChange
    }
  } = useField('deposit', { subscription: { value: true } });

  const closePrompt = () => {
    onClose();
    onCloseCallback();
  };

  const convertedAmount = convertAmountByRate(
    Number(amount),
    getCurrencyRate(rates, currency),
    currency,
    false
  );

  const convertedAmountInCounrtyCurrency = convertAmountByRate(
    Number(convertedAmount),
    getCurrencyRate(rates, defaultCountryCurrency),
    defaultCountryCurrency,
    true
  );

  return (
    <Prompt
      variant="info"
      title={{
        translation: TXTWCL_Your_deposit_will_be_converted
      }}
      primaryButton={{
        translation: TXTCLI_Deposit_in_N0,
        values: { 0: defaultCountryCurrency }
      }}
      onSecondaryButtonClick={closePrompt}
      secondaryButton={{ translation: TXTCLI_Deposit_in_N0, values: { 0: currency } }}
      onPrimaryButtonClick={() => {
        onChange({
          amount: convertedAmountInCounrtyCurrency,
          currency: defaultCountryCurrency,
          ...rest
        });
        closePrompt();
      }}
      onClose={onClose}
    >
      <div className={multiContent}>
        <Translation tagName="p" {...TXTWCL_Your_bank_may_charge_you_a_for_ELL_1} />
        <Translation
          tagName="p"
          {...TXTWCL_Our_exchange_rate_is_N0_to_N1X}
          values={{
            0: (
              <CurrencyAmount
                value={1}
                currency={currency}
                currencyDisplay="code"
                dollarValue
                isZeroExponentCurrency={isZeroExponentCurrency(currency)}
              />
            ),
            1: (
              <CurrencyAmount
                value={convertAmountByRate(
                  1,
                  getCurrencyRate(
                    rates,
                    defaultCountryCurrency !== balanceCurrency ? defaultCountryCurrency : currency
                  ),
                  defaultCountryCurrency !== balanceCurrency ? defaultCountryCurrency : currency,
                  defaultCountryCurrency !== balanceCurrency ? true : false,
                  4
                )}
                currency={defaultCountryCurrency}
                currencyDisplay="code"
                dollarValue
                isZeroExponentCurrency={isDefaultCurrencyZeroExponent}
              />
            )
          }}
        />
        <Translation
          tagName="p"
          {...TXTWCL_Would_you_like_to_change_your__ELL}
          values={{ 0: defaultCountryCurrency }}
        />
      </div>
    </Prompt>
  );
};

export default LocalCurrency;
